import React, { Suspense, lazy } from 'react';
import { Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useSite } from '../../contexts/SiteContext';
import BTTNavigation from './components/BTTNavigation';
import BTTFooter from './components/BTTFooter';

// Eager: the homepage is the LCP target on first paint — keep it in the
// main chunk so we don't pay a Suspense round-trip on cold load.
import BTTHomePage from './pages/BTTHomePage';
// Eager: BTTAuthCallback is rendered synchronously when the URL hash carries
// a Google OAuth `session_id=…`. We can't lazy-load it without flashing.
import BTTAuthCallback from './pages/BTTAuthCallback';

// Redirects the browser to an external URL (used for short vanity aliases)
const ExternalRedirect = ({ to }) => {
  React.useEffect(() => { window.location.replace(to); }, [to]);
  return null;
};

// Everything else is split into its own JS chunk and only downloaded when
// the visitor actually navigates there. Cuts initial JS payload by ~65%
// and makes mobile first-paint dramatically faster.
const PhilosophyPage = lazy(() => import('./pages/PhilosophyPage'));
const EditorialToolPage = lazy(() => import('./pages/EditorialToolPage'));
const BookPage = lazy(() => import('./pages/BookPage'));
const ProgramPage = lazy(() => import('./pages/ProgramPage'));
const CaseStudiesPage = lazy(() => import('./pages/CaseStudiesPage'));
const CaseStudyPage = lazy(() => import('./pages/CaseStudyPage'));
const PodcastPage = lazy(() => import('./pages/PodcastPage'));
const CommunityPage = lazy(() => import('./pages/CommunityPage'));
const ResourcesPage = lazy(() => import('./pages/ResourcesPage'));
const ApplyPage = lazy(() => import('./pages/ApplyPage'));
const BTTCreatorPage = lazy(() => import('./pages/BTTCreatorPage'));
const BTTProjectPage = lazy(() => import('./pages/BTTProjectPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const PressPage = lazy(() => import('./pages/PressPage'));
const StorePage = lazy(() => import('./pages/StorePage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const SignupPage = lazy(() => import('./pages/SignupPage'));
const CreatorDashboard = lazy(() => import('./pages/CreatorDashboard'));
const ScheduleApplyPage = lazy(() => import('./pages/ScheduleApplyPage'));
const CreatorLabPublicPage = lazy(() => import('./pages/CreatorLabPublicPage'));
const CreatorLabPrivatePage = lazy(() => import('./pages/CreatorLabPrivatePage'));
const UnstuckPage = lazy(() => import('./pages/UnstuckPage'));
const TestimonialsPage = lazy(() => import('./pages/TestimonialsPage'));
const UpcomingCoursesPage = lazy(() => import('./pages/UpcomingCoursesPage'));
const YouMeHerPage = lazy(() => import('./pages/YouMeHerPage'));
const YMHProposalPage = lazy(() => import('./pages/YMHProposalPage'));
const YMHCaseStudyPage = lazy(() => import('./pages/YMHCaseStudyPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const TuEresElTrenPage = lazy(() => import('./pages/TuEresElTrenPage'));
const StartTheEngineBookPage = lazy(() => import('./pages/StartTheEngineBookPage'));

import { useKickerReveal } from './lib/useKickerReveal';

/**
 * Be The Train App Shell
 * Standalone site with its own routing, navigation, and styling
 */
const BTTApp = () => {
  const { siteConfig, loading } = useSite();
  const location = useLocation();

  // Mount global kicker-underline reveal observer (Chef's Kiss #3)
  useKickerReveal();

  // Check URL fragment for session_id (Google OAuth callback) - must be synchronous
  if (location.hash?.includes('session_id=')) {
    return <BTTAuthCallback />;
  }
  
  const isFullscreenPage = location.pathname === '/platform/app';
  
  // BTT branding
  const bttLogo = 'https://customer-assets.emergentagent.com/job_5fff2c07-dfff-4220-a200-8d4fd9f6d613/artifacts/i6xi2h68_BTTLogo.png';
  const bttOgImage = 'https://customer-assets.emergentagent.com/job_c616a873-a54c-4989-849b-bca749e896c0/artifacts/1ahrvzl8_BeTheTrain_2026x1126x_Banner.jpg';
  
  if (loading) {
    return <BTTLoadingScreen />;
  }
  
  return (
    <div className="btt-app min-h-screen bg-black text-white">
      {/* Global 35mm grain overlay — Chef's Kiss */}
      <div className="btt-grain" aria-hidden="true" data-testid="btt-grain-overlay" />

      {/* SEO Meta Tags for Be The Train */}
      <Helmet>
        <title>Be The Train | Creator Lab</title>
        <meta name="description" content="Stop waiting for permission. Start your own momentum. Be The Train is a creator lab for filmmakers and storytellers who want to launch projects and take control of their creative careers." />
        
        {/* Open Graph */}
        <meta property="og:title" content="Be The Train | Creator Lab" />
        <meta property="og:description" content="Stop waiting for permission. Start your own momentum. A creator lab for filmmakers and storytellers." />
        <meta property="og:image" content={bttOgImage} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Be The Train" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Be The Train | Creator Lab" />
        <meta name="twitter:description" content="Stop waiting for permission. Start your own momentum." />
        <meta name="twitter:image" content={bttOgImage} />
        
        {/* Favicon */}
        <link rel="icon" href={bttLogo} />
        <link rel="apple-touch-icon" href={bttLogo} />
        
        {/* Theme */}
        <meta name="theme-color" content="#00A86B" />
      </Helmet>
      
      {!isFullscreenPage && <BTTNavigation />}
      
      <main>
        <Suspense fallback={<BTTLoadingScreen />}>
          <Routes>
          {/* Main pages */}
          <Route path="/" element={<BTTHomePage />} />
          <Route path="/philosophy" element={<PhilosophyPage />} />
          <Route path="/book" element={<BookPage />} />
          <Route path="/books/start-the-engine" element={<StartTheEngineBookPage />} />
          <Route path="/tu-eres-el-tren" element={<TuEresElTrenPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/press" element={<PressPage />} />
          <Route path="/store" element={<StorePage />} />
          <Route path="/testimonials" element={<TestimonialsPage />} />
          {/* Legacy /accelerator removed — redirect to Creator Lab */}
          <Route path="/accelerator" element={<Navigate to="/creator-lab" replace />} />
          
          {/* Program pages */}
          <Route path="/the-station" element={<ProgramPage programSlug="the-station" />} />
          <Route path="/creator-lab" element={<CreatorLabPublicPage />} />
          <Route path="/creator-lab-inside" element={<CreatorLabPrivatePage />} />
          <Route path="/unstuck" element={<UnstuckPage />} />
          <Route path="/upcoming-courses" element={<UpcomingCoursesPage />} />
          {/* /starttheengine is the OLD manifesto landing page — redirect to the canonical book page */}
          <Route path="/starttheengine" element={<Navigate to="/books/start-the-engine" replace />} />
          {/* Common aliases for the manifesto */}
          <Route path="/start-the-engine" element={<Navigate to="/books/start-the-engine" replace />} />
          <Route path="/manifesto" element={<Navigate to="/books/start-the-engine" replace />} />
          <Route path="/ymhmexico" element={<YouMeHerPage />} />
          <Route path="/ymhproposal" element={<YMHProposalPage />} />
          {/* Legacy alias */}
          <Route path="/you-me-her" element={<Navigate to="/ymhmexico" replace />} />
          <Route path="/full-steam" element={<ProgramPage programSlug="full-steam" />} />
          <Route path="/engine-room" element={<ProgramPage programSlug="engine-room" />} />
          
          {/* Content pages */}
          <Route path="/case-studies" element={<CaseStudiesPage />} />
          <Route path="/case-studies/you-me-and-her" element={<YMHCaseStudyPage />} />
          <Route path="/case-studies/:slug" element={<CaseStudyPage />} />
          <Route path="/podcast" element={<PodcastPage />} />
          <Route path="/podcast/:slug" element={<PodcastPage />} />
          <Route path="/resources" element={<ResourcesPage />} />
          
          {/* Community / Infrastructure */}
          <Route path="/community" element={<CommunityPage />} />
          <Route path="/creators/:slug" element={<BTTCreatorPage />} />
          <Route path="/projects/:slug" element={<BTTProjectPage />} />
          
          {/* Application */}
          <Route path="/apply" element={<ApplyPage />} />
          <Route path="/book-a-call" element={<ScheduleApplyPage />} />
          {/* Internal editorial tool — access-code gated, noindex, not linked from nav */}
          <Route path="/editorialtool" element={<EditorialToolPage />} />
          {/* /bookacall is a short alias that jumps straight to the HighLevel booking page */}
          <Route path="/bookacall" element={<ExternalRedirect to="https://bethetrain.x20.io/booking-page-6795-729766-1382" />} />
          {/* Legacy /schedule redirect to /book-a-call */}
          <Route path="/schedule" element={<Navigate to="/book-a-call" replace />} />
          
          {/* Platform routes disabled — tool not available right now */}
          <Route path="/platform" element={<Navigate to="/" replace />} />
          <Route path="/platform/app" element={<Navigate to="/" replace />} />
          <Route path="/platform/studio" element={<Navigate to="/" replace />} />
          <Route path="/platform/studio/:projectId" element={<Navigate to="/" replace />} />
          <Route path="/platform/*" element={<Navigate to="/" replace />} />
          
          {/* Auth */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/dashboard" element={<CreatorDashboard />} />
          
          {/* Catch-all for unknown routes */}
          <Route path="*" element={<BTT404Page />} />
        </Routes>
        </Suspense>
      </main>
      
      {!isFullscreenPage && <BTTFooter />}
    </div>
  );
};

/**
 * Loading screen for BTT
 */
const BTTLoadingScreen = () => (
  <div className="min-h-screen bg-black flex items-center justify-center">
    <div className="text-center">
      <div className="w-16 h-16 border-4 border-[#00A86B] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
      <p className="text-[#00A86B] text-sm uppercase tracking-widest">Loading</p>
    </div>
  </div>
);

/**
 * 404 page for BTT
 */
const BTT404Page = () => (
  <div className="min-h-screen bg-black flex items-center justify-center">
    <div className="text-center">
      <h1 className="text-6xl font-bold text-[#00A86B] mb-4">404</h1>
      <p className="text-white/60 mb-8">Page not found</p>
      <a 
        href="/" 
        className="inline-block px-8 py-3 bg-[#00A86B] text-black font-bold uppercase tracking-wider hover:bg-[#00A86B]/90 transition-colors"
      >
        Back to Home
      </a>
    </div>
  </div>
);

export default BTTApp;
